import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Smart back-navigation. Returns a `goBack(fallback?)` function that:
 *
 *   1. If the user arrived here via `navigate("...", { state: { from: "..." } })`,
 *      returns them to that exact path.
 *   2. Else if there's real in-app history AND we're not at the entry route,
 *      does `navigate(-1)` (browser back).
 *   3. Else falls back to the provided `fallback` route (or `/dashboard`).
 *
 * Also exposes `linkStateWithFrom()` — a helper that returns
 * `{ state: { from: currentPathAndSearch } }` so any `navigate(url, ...)` call
 * from THIS page will let the next page bounce back correctly.
 *
 * Usage:
 *   const { goBack, linkStateWithFrom } = useSmartBack();
 *   ...
 *   <Button onClick={() => goBack("/projects")}>Back</Button>
 *   <Button onClick={() => navigate(`/contacts/${id}`, linkStateWithFrom())}>
 *     View Contact
 *   </Button>
 */
export function useSmartBack() {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = useCallback((fallback = "/dashboard") => {
    // Preferred: explicit `from` state (set by the caller when navigating in)
    const from = location?.state?.from;
    if (from && typeof from === "string" && from.startsWith("/")) {
      navigate(from);
      return;
    }
    // Next best: browser back if there's real history (idx > 0)
    // window.history.length > 1 is unreliable across browsers, but the
    // `key` on location changes for every real navigation — if key !== "default",
    // this location was pushed by us and back() will land inside the app.
    if (location?.key && location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(fallback);
  }, [navigate, location]);

  const linkStateWithFrom = useCallback((extraState = {}) => {
    const current = `${location.pathname}${location.search || ""}`;
    return { state: { from: current, ...extraState } };
  }, [location]);

  return { goBack, linkStateWithFrom };
}

export default useSmartBack;
